/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';
import * as _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import { eSurveyQuestionCategoryName, iSurveyResponse, iSurveyQuestionAmountEntry } from '../../../../apiServices/SurveyService';
import Card, { CardSection } from '../../../components/card/Card';
import { SelectableCardRowWithGroupSurveyTheme } from '../../../components/SelectableRow';
import ExpandableNestedQuestion from './components/ExpandableNestedQuestion';
import InputWithLabel from '../../../components/InputWithLabel';
import AppUtils from '../../../../utilities/AppUtils';

import './AmountEntryQuestion.scss';
import BackpackSource from './components/BackpackSource';
import InputErrorText from '../../../components/InputErrorText';

const NA_UNIT = {
  id: null,
  components: [' '],
};

interface Props {
  onUpdate(data): void;
  question: any;
  response: any;
  profileId: number;
  showValidationError: boolean;
  clearError(): void;
}

function transformIntoSingleAnswerQuestion(units, selectedUnit) {
  return {
    question: {
      category: eSurveyQuestionCategoryName.singleAnswer,
      text: <FormattedMessage id="surveyQuestion.changeUnit.button.label" />,
      [eSurveyQuestionCategoryName.singleAnswer]: {
        options: units.map(u => ({
          id: u.id,
          response: u,
          text: u.text,
        })),
      },
    },
    response: {
      [eSurveyQuestionCategoryName.singleAnswer]: {
        id: selectedUnit.id,
      },
    },
  };
}

function getSelectedUnit(units, unitId) {
  if (units.length === 0) {
    return NA_UNIT;
  } else if (units.length === 1) {
    return units[0];
  } else {
    return units.find((u) => u.id === unitId) || units[0];
  }
}
export const NestedAmountEntryQuestion = (props: Props) => {
  const { question, response, profileId, showValidationError, clearError, onUpdate } = props;
  const amountEntryResponse = response[eSurveyQuestionCategoryName.amountEntry] || {};
  const amountEntryQuestion = question[eSurveyQuestionCategoryName.amountEntry] || {};
  const units = amountEntryQuestion.units || [];
  const values = amountEntryResponse.values || [''];
  const selectedUnit = getSelectedUnit(units, amountEntryResponse.unit_id);

  const handleUpdate = (partialData) => {
    const amountEntryResponse = response[eSurveyQuestionCategoryName.amountEntry] || {};

    const newResponse = {
      ...response,
      category: eSurveyQuestionCategoryName.amountEntry,
      [eSurveyQuestionCategoryName.amountEntry]: {
        ...amountEntryResponse,
        ...partialData,
      },
    };

    onUpdate(newResponse);
  };
  const displaySourceUnit = (units) => {
    const amountEntryQuestion = question[eSurveyQuestionCategoryName.amountEntry] || {};

    if (!amountEntryQuestion.source) { return ''; }

    const sourceUnit = _.find(units, (u) => u.id === amountEntryQuestion.source.unit_id);
    const sourceValues = amountEntryQuestion.source.values;

    if (!sourceUnit) { return sourceValues; }

    return sourceValues.map((v, i) => `${v} ${sourceUnit.components[i]}`).join(' ');
  };

  return (
    <>
      <Card>
        <CardSection hasBorder>
          {
            selectedUnit.components.map((unitText, i) => (
              <div className="cmp-amountentryquestion" key={i}>
                <div className="cmp-amountentryquestion--input">
                  <InputWithLabel
                    value={values[i] || ''}
                    onFocus={clearError}
                    onChange={(event) => {
                      const newValues = [...values];
                      newValues[i] = event.target.value;

                      handleUpdate({
                        values: newValues,
                        unit_id: selectedUnit.id,
                        unsure: false,
                      });
                    }}
                    isInvalid={showValidationError}
                    type={AppUtils.isIosDevice() ? 'text' : 'tel'}
                    name={`amount_value_${i}`}
                    regularHint={showValidationError ? null : <span className="font-color-gsMedDark">{amountEntryQuestion.hint_text}</span>}
                    style={{ maxWidth: 56 }}
                  />
                </div>
                <label htmlFor={`amount_value_${i}`}>
                  {unitText}
                </label>
              </div>
            ))
          }
          <div>
            {
              showValidationError && (
                <InputErrorText style={{ position: 'absolute', marginTop: -39 }}>
                  <FormattedMessage id="error.number-validator.invalid-value.message" />
                </InputErrorText>
              )
            }
          </div>
          <BackpackSource
            source={amountEntryQuestion.source}
            value={displaySourceUnit(units)}
            profileId={profileId}
          />
        </CardSection>

        {
          units.length > 1 && (
            <ExpandableNestedQuestion
              defaultExpanded={false}
              {...transformIntoSingleAnswerQuestion(units, selectedUnit)}
              valueText=""
              onUpdate={(singleAnswerResponse, option) => {
                handleUpdate({
                  unit_id: option.id,
                  values: values.slice(0, option.response.components.length),
                  unsure: false,
                });
              }}
            />
          )
        }
      </Card>

      <Card>
        <SelectableCardRowWithGroupSurveyTheme
          className="font-body"
          isSelected={amountEntryResponse.unsure}
          onClick={() => {
            handleUpdate({
              values: null,
              unsure: !amountEntryResponse.unsure,
            });
          }}
        >
          <FormattedMessage id="surveyQuestion.valueUnit.unsure.button.label" />
        </SelectableCardRowWithGroupSurveyTheme>
      </Card>
    </>
  );
};

const AmountEntryQuestion = (props) => (
  <NestedAmountEntryQuestion {...props} />
);

interface AmountEntryAnswerProps {
  className: string;
  style: object;
  answer: iSurveyResponse;
  question: iSurveyQuestionAmountEntry;
}

export const AmountEntryAnswer = (props: AmountEntryAnswerProps) => {
  const answer = props.answer[eSurveyQuestionCategoryName.amountEntry];
  const units = props.question[eSurveyQuestionCategoryName.amountEntry].units || [];
  const selectedUnit = _.find(units, u => u.id === answer.unit_id) || null;

  if (answer.unsure) {
    return (
      <div className={props.className} style={props.style}>
        <FormattedMessage id="surveyQuestion.valueUnit.unsure.button.label" />
      </div>
    );
  } else if (!selectedUnit) {
    return (
      <div className={props.className} style={props.style}>
        {answer.values.join(' ')}
      </div>
    );
  } else {
    return (
      <div className={props.className} style={props.style}>
        {answer.values.map((v, i) => `${v} ${selectedUnit.components[i]}`).join(' ')}
      </div>
    );
  }
};

export default AmountEntryQuestion;
